import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { SITE, SITE_STUDIO_TYPE } from "@/config";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatEuro, formatHour, formatEndHour, STATUS_LABELS, STUDIO_LABELS } from "@/lib/api";
import {
  Lock,
  Loader2,
  LogOut,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Banknote,
  Euro,
  Hourglass,
  Wrench,
  Trash2,
  UserX,
  Mail,
  Settings2,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";

const STATUS_BADGES = {
  en_attente_validation: "border-orange-500/40 bg-orange-500/10 text-orange-400",
  en_attente_paiement: "border-primary/40 bg-[hsl(var(--studio-yellow)/0.08)] text-primary",
  confirmee: "border-green-600/40 bg-green-600/10 text-green-400",
  reservee: "border-border bg-secondary text-foreground",
  annulee: "border-destructive/40 bg-destructive/10 text-destructive",
};

const EMAIL_STATUS_BADGES = {
  envoye: "border-green-600/40 bg-green-600/10 text-green-400",
  non_configure: "border-orange-500/40 bg-orange-500/10 text-orange-400",
  erreur: "border-destructive/40 bg-destructive/10 text-destructive",
};

const EMAIL_STATUS_LABELS = {
  envoye: "Envoyé",
  non_configure: "SMTP non configuré",
  erreur: "Erreur",
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockedClients, setBlockedClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ studio_type: "all", status: "all", date: "" });
  const [priceEdits, setPriceEdits] = useState({});
  const [settingsEdits, setSettingsEdits] = useState({});
  const [slotForm, setSlotForm] = useState({ studio_type: SITE_STUDIO_TYPE, date: "", start_hour: "9", end_hour: "12", reason: "" });
  const [clientForm, setClientForm] = useState({ email: "", reason: "" });

  const authHeaders = useCallback(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setToken(null);
  }, []);

  const handleAuthError = useCallback(
    (e) => {
      if (e.response?.status === 401) {
        toast.error("Session expirée, reconnectez-vous");
        logout();
        return true;
      }
      return false;
    },
    [logout]
  );

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (filters.studio_type !== "all") params.studio_type = filters.studio_type;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.date) params.date = filters.date;
      const [statsRes, bookingsRes, servicesRes, slotsRes, clientsRes, settingsRes, logsRes] = await Promise.all([
        api.get("/admin/stats", authHeaders()),
        api.get("/admin/bookings", { ...authHeaders(), params }),
        api.get("/admin/services", authHeaders()),
        api.get("/admin/blocked-slots", authHeaders()),
        api.get("/admin/blocked-clients", authHeaders()),
        api.get("/admin/settings", authHeaders()),
        api.get("/admin/email-logs", authHeaders()),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
      setBlockedSlots(slotsRes.data);
      setBlockedClients(clientsRes.data);
      setSettings(settingsRes.data);
      setEmailLogs(logsRes.data);
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [token, filters, authHeaders, handleAuthError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoggingIn(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const { data } = await api.put(`/admin/bookings/${bookingId}/status`, { status }, authHeaders());
      const messages = {
        reservee: "Réservation validée — email de confirmation envoyé au client",
        confirmee: "Réservation marquée comme payée",
        annulee:
          data?.refunded
            ? "Réservation annulée — remboursement Stripe automatique effectué, email envoyé au client"
            : "Réservation annulée — email envoyé au client",
      };
      toast.success(messages[status] || "Réservation mise à jour");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la mise à jour");
    }
  };

  const blockClientFromBooking = async (booking) => {
    try {
      await api.post(
        "/admin/blocked-clients",
        {
          email: booking.customer?.email || "",
          phone: booking.customer?.phone || "",
          name: booking.customer?.name || "",
          reason: `Bloqué depuis la réservation ${booking.id.slice(0, 8)}`,
        },
        authHeaders()
      );
      toast.success(`Client ${booking.customer?.email} bloqué`);
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error(e.response?.data?.detail || "Erreur lors du blocage");
    }
  };

  const savePrices = async (svc) => {
    const edits = priceEdits[svc.id] || {};
    const body = {};
    if (edits.price_onsite !== undefined && edits.price_onsite !== "") {
      body.price_onsite = parseFloat(edits.price_onsite);
    }
    if (Object.keys(body).length === 0) {
      toast.info("Aucune modification");
      return;
    }
    try {
      await api.put(`/admin/services/${svc.id}`, body, authHeaders());
      toast.success(`Tarif mis à jour : ${svc.name}`);
      setPriceEdits((prev) => ({ ...prev, [svc.id]: {} }));
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la mise à jour du tarif");
    }
  };

  const createBlockedSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.date) {
      toast.error("Choisissez une date");
      return;
    }
    try {
      await api.post(
        "/admin/blocked-slots",
        {
          studio_type: slotForm.studio_type,
          date: slotForm.date,
          start_hour: parseInt(slotForm.start_hour, 10),
          end_hour: parseInt(slotForm.end_hour, 10),
          reason: slotForm.reason || "Maintenance",
        },
        authHeaders()
      );
      toast.success("Créneaux bloqués");
      setSlotForm({ ...slotForm, date: "", reason: "" });
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteBlockedSlot = async (id) => {
    try {
      await api.delete(`/admin/blocked-slots/${id}`, authHeaders());
      toast.success("Indisponibilité supprimée — créneaux réouverts");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de la suppression");
    }
  };

  const blockClientManual = async (e) => {
    e.preventDefault();
    try {
      await api.post(
        "/admin/blocked-clients",
        { email: clientForm.email, reason: clientForm.reason },
        authHeaders()
      );
      toast.success(`Client ${clientForm.email} bloqué`);
      setClientForm({ email: "", reason: "" });
      fetchAll();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const unblockClient = async (id) => {
    try {
      await api.delete(`/admin/blocked-clients/${id}`, authHeaders());
      toast.success("Client débloqué");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur");
    }
  };

  const saveSettings = async () => {
    const body = {};
    if (settingsEdits.manual_confirmation !== undefined) body.manual_confirmation = settingsEdits.manual_confirmation;
    if (settingsEdits.online_discount_percent !== undefined && settingsEdits.online_discount_percent !== "")
      body.online_discount_percent = parseFloat(settingsEdits.online_discount_percent);
    if (settingsEdits.company_email_enregistrement !== undefined && settingsEdits.company_email_enregistrement !== "")
      body.company_email_enregistrement = settingsEdits.company_email_enregistrement;
    if (settingsEdits.company_email_media !== undefined && settingsEdits.company_email_media !== "")
      body.company_email_media = settingsEdits.company_email_media;
    ["social_instagram", "social_tiktok", "social_snapchat", "contact_phone"].forEach((k) => {
      if (settingsEdits[k] !== undefined && settingsEdits[k] !== "") body[k] = settingsEdits[k];
    });
    if (Object.keys(body).length === 0) {
      toast.info("Aucune modification");
      return;
    }
    try {
      const res = await api.put("/admin/settings", body, authHeaders());
      setSettings(res.data);
      setSettingsEdits({});
      toast.success("Paramètres enregistrés");
      fetchAll();
    } catch (e) {
      if (!handleAuthError(e)) toast.error("Erreur lors de l'enregistrement");
    }
  };

  // ---------- Login ----------
  if (!token) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col px-4 py-20 sm:px-6">
        <Card className="border-border bg-card" data-testid="admin-login-card">
          <CardContent className="p-6 sm:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary">
              <Lock className="h-5 w-5 text-primary-foreground" />
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold">Accès admin</h1>
            <p className="mt-1 text-sm text-muted-foreground">Espace réservé à FNK STUDIO.</p>
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-password">Mot de passe</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loggingIn || !password} data-testid="admin-login-submit">
                {loggingIn ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Tableau de bord</h1>
          <p className="mt-1 text-sm text-muted-foreground">FNK STUDIO — gestion des réservations</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout-button">
          <LogOut className="mr-2 h-4 w-4" />
          Déconnexion
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
        {[
          { label: "Réservations", value: stats?.total ?? "—", icon: CalendarDays },
          { label: "À valider", value: stats?.a_valider ?? "—", icon: Hourglass, highlight: (stats?.a_valider || 0) > 0 },
          { label: "Confirmées / Payées", value: stats?.confirmees ?? "—", icon: CheckCircle2 },
          { label: "Paiement sur place", value: stats?.reservees ?? "—", icon: Banknote },
          { label: "Chiffre d'affaires", value: stats ? formatEuro(stats.revenue) : "—", icon: Euro },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className={`bg-card ${stat.highlight ? "border-orange-500/50" : "border-border"}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {stat.label}
                  </span>
                  <Icon className={`h-4 w-4 ${stat.highlight ? "text-orange-400" : "text-primary"}`} />
                </div>
                <p className="mt-2 font-mono-nums text-2xl font-semibold">{stat.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="reservations" className="mt-10">
        <TabsList className="h-auto flex-wrap bg-secondary">
          <TabsTrigger value="reservations" data-testid="admin-tab-reservations">Réservations</TabsTrigger>
          <TabsTrigger value="tarifs" data-testid="admin-tab-tarifs">Tarifs</TabsTrigger>
          <TabsTrigger value="indisponibilites" data-testid="admin-tab-indisponibilites">Indisponibilités</TabsTrigger>
          <TabsTrigger value="clients" data-testid="admin-tab-clients">Clients bloqués</TabsTrigger>
          <TabsTrigger value="parametres" data-testid="admin-tab-parametres">Paramètres</TabsTrigger>
        </TabsList>

        {/* ---------- Reservations ---------- */}
        <TabsContent value="reservations" className="mt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Statut</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="w-[220px]" data-testid="admin-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="en_attente_validation">En attente de validation</SelectItem>
                  <SelectItem value="confirmee">Confirmée / Payée</SelectItem>
                  <SelectItem value="reservee">Confirmée (sur place)</SelectItem>
                  <SelectItem value="en_attente_paiement">En attente de paiement</SelectItem>
                  <SelectItem value="annulee">Annulée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Date</Label>
              <Input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                className="w-[170px]"
                data-testid="admin-filter-date"
              />
            </div>
            {filters.date && (
              <Button variant="ghost" size="sm" onClick={() => setFilters({ ...filters, date: "" })}>
                Effacer la date
              </Button>
            )}
          </div>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table data-testid="admin-reservations-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Horaire</TableHead>
                  <TableHead>Espace</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && bookings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                      Aucune réservation trouvée
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  bookings.map((b) => (
                    <TableRow key={b.id} className="border-border" data-testid={`admin-booking-row-${b.id}`}>
                      <TableCell className="whitespace-nowrap capitalize">
                        {format(parseISO(b.date), "EEE d MMM", { locale: fr })}
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono-nums">
                        {formatHour(b.start_hour)}–{formatEndHour(b.end_hour)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{STUDIO_LABELS[b.studio_type]}</TableCell>
                      <TableCell className="max-w-[170px] text-xs">
                        <p className="truncate">{b.service_name}</p>
                        {(b.addons || []).length > 0 && (
                          <p className="truncate text-[11px] text-primary">
                            + {b.addons.map((a) => a.name).join(", ")}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        <p className="truncate text-sm">{b.customer?.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {b.customer?.phone} · {b.customer?.email}
                        </p>
                      </TableCell>
                      <TableCell className="whitespace-nowrap font-mono-nums font-medium">
                        {formatEuro(b.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`whitespace-nowrap text-[11px] ${STATUS_BADGES[b.status] || ""}`}>
                          {STATUS_LABELS[b.status] || b.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          {b.status === "en_attente_validation" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-green-600/40 text-green-400 hover:bg-green-600/10"
                              onClick={() => updateBookingStatus(b.id, "reservee")}
                              data-testid={`admin-validate-${b.id}`}
                              title="Valider (email de confirmation envoyé)"
                            >
                              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                              Valider
                            </Button>
                          )}
                          {b.status === "reservee" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-green-600/40 text-green-400 hover:bg-green-600/10"
                              onClick={() => updateBookingStatus(b.id, "confirmee")}
                              data-testid={`admin-confirm-${b.id}`}
                              title="Marquer comme payée"
                            >
                              <Euro className="mr-1 h-3.5 w-3.5" />
                              Payée
                            </Button>
                          )}
                          {b.status !== "annulee" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 border-destructive/40 text-destructive hover:bg-destructive/10"
                              onClick={() => updateBookingStatus(b.id, "annulee")}
                              data-testid={`admin-cancel-${b.id}`}
                              title="Annuler (email envoyé au client)"
                            >
                              <XCircle className="mr-1 h-3.5 w-3.5" />
                              Annuler
                            </Button>
                          )}
                          {b.status === "annulee" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-muted-foreground"
                              onClick={() => updateBookingStatus(b.id, "reservee")}
                              data-testid={`admin-restore-${b.id}`}
                            >
                              <Hourglass className="mr-1 h-3.5 w-3.5" />
                              Réactiver
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-muted-foreground hover:text-destructive"
                            onClick={() => blockClientFromBooking(b)}
                            data-testid={`admin-block-client-${b.id}`}
                            title="Bloquer ce client"
                          >
                            <UserX className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- Tarifs ---------- */}
        <TabsContent value="tarifs" className="mt-6">
          <p className="mb-4 text-sm text-muted-foreground">
            Modifiez le tarif <span className="font-medium text-foreground">sur place</span> : le tarif en
            ligne est calculé automatiquement avec la remise de{" "}
            <span className="text-primary">-{settings?.online_discount_percent ?? 0}%</span> (modifiable
            dans Paramètres).
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {services.map((svc) => {
              const edits = priceEdits[svc.id] || {};
              return (
                <Card key={svc.id} className="border-border bg-card" data-testid={`admin-service-${svc.slug}`}>
                  <CardContent className="p-5">
                    <h3 className="font-heading text-base font-semibold">{svc.name}</h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {STUDIO_LABELS[svc.studio_type]} ·{" "}
                      {svc.pricing_type === "hourly" ? "Tarif horaire" : `Forfait ${svc.duration_hours}h`}
                      {svc.from_price ? " · à partir de" : ""}
                    </p>
                    <div className="mt-4 grid grid-cols-2 items-end gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">
                          Sur place {svc.pricing_type === "hourly" ? "(€/h)" : "(€)"}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={String(svc.price_onsite)}
                          value={edits.price_onsite ?? ""}
                          onChange={(e) =>
                            setPriceEdits((prev) => ({
                              ...prev,
                              [svc.id]: { ...prev[svc.id], price_onsite: e.target.value },
                            }))
                          }
                          data-testid={`admin-price-onsite-${svc.slug}`}
                        />
                      </div>
                      <div className="rounded-md bg-[hsl(var(--studio-yellow)/0.10)] px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-primary">En ligne (auto)</p>
                        <p className="font-mono-nums font-semibold text-primary">
                          {formatEuro(svc.price_online)}
                          {svc.pricing_type === "hourly" ? "/h" : ""}
                        </p>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Actuel : {formatEuro(svc.price_onsite)}
                      {svc.pricing_type === "hourly" ? "/h" : ""} sur place
                    </p>
                    <Button size="sm" className="mt-3" onClick={() => savePrices(svc)} data-testid={`admin-save-price-${svc.slug}`}>
                      Enregistrer
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ---------- Indisponibilites ---------- */}
        <TabsContent value="indisponibilites" className="mt-6">
          <Card className="border-border bg-card">
            <CardContent className="p-5 sm:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                <Wrench className="h-4 w-4 text-primary" />
                Bloquer des créneaux (maintenance, absence…)
              </h3>
              <form onSubmit={createBlockedSlot} className="mt-4 flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Date</Label>
                  <Input
                    type="date"
                    value={slotForm.date}
                    onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })}
                    className="w-[160px]"
                    data-testid="blocked-slot-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">De</Label>
                  <Select value={slotForm.start_hour} onValueChange={(v) => setSlotForm({ ...slotForm, start_hour: v })}>
                    <SelectTrigger className="w-[100px]" data-testid="blocked-slot-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">À</Label>
                  <Select value={slotForm.end_hour} onValueChange={(v) => setSlotForm({ ...slotForm, end_hour: v })}>
                    <SelectTrigger className="w-[100px]" data-testid="blocked-slot-end">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((h) => (
                        <SelectItem key={h + 1} value={String(h + 1)}>{h + 1 === 24 ? "24:00" : formatHour(h + 1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5" style={{ minWidth: "160px" }}>
                  <Label className="text-xs text-muted-foreground">Raison</Label>
                  <Input
                    placeholder="Maintenance console…"
                    value={slotForm.reason}
                    onChange={(e) => setSlotForm({ ...slotForm, reason: e.target.value })}
                    data-testid="blocked-slot-reason"
                  />
                </div>
                <Button type="submit" data-testid="blocked-slot-submit">Bloquer</Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table data-testid="blocked-slots-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Horaire</TableHead>
                  <TableHead>Espace</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedSlots.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Aucune indisponibilité programmée
                    </TableCell>
                  </TableRow>
                )}
                {blockedSlots.map((s) => (
                  <TableRow key={s.id} className="border-border">
                    <TableCell className="capitalize">
                      {format(parseISO(s.date), "EEE d MMM yyyy", { locale: fr })}
                    </TableCell>
                    <TableCell className="font-mono-nums">
                      {formatHour(s.start_hour)}–{s.end_hour === 24 ? "24:00" : formatHour(s.end_hour)}
                    </TableCell>
                    <TableCell className="text-xs">{STUDIO_LABELS[s.studio_type]}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">{s.reason}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteBlockedSlot(s.id)}
                        data-testid={`delete-blocked-slot-${s.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- Clients bloques ---------- */}
        <TabsContent value="clients" className="mt-6">
          <Card className="border-border bg-card">
            <CardContent className="p-5 sm:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                <UserX className="h-4 w-4 text-primary" />
                Bloquer un client (par email)
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Un client bloqué ne peut plus effectuer de réservation (en ligne ou sur place).
              </p>
              <form onSubmit={blockClientManual} className="mt-4 flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Email du client</Label>
                  <Input
                    type="email"
                    placeholder="client@exemple.fr"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    className="w-[240px]"
                    data-testid="block-client-email"
                  />
                </div>
                <div className="flex-1 space-y-1.5" style={{ minWidth: "160px" }}>
                  <Label className="text-xs text-muted-foreground">Raison (optionnel)</Label>
                  <Input
                    placeholder="No-show répété…"
                    value={clientForm.reason}
                    onChange={(e) => setClientForm({ ...clientForm, reason: e.target.value })}
                    data-testid="block-client-reason"
                  />
                </div>
                <Button type="submit" disabled={!clientForm.email} data-testid="block-client-submit">
                  Bloquer
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table data-testid="blocked-clients-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Email</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blockedClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Aucun client bloqué
                    </TableCell>
                  </TableRow>
                )}
                {blockedClients.map((c) => (
                  <TableRow key={c.id} className="border-border">
                    <TableCell className="text-sm">{c.email}</TableCell>
                    <TableCell className="text-sm">{c.name || "—"}</TableCell>
                    <TableCell className="font-mono-nums text-sm">{c.phone || "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                      {c.reason || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => unblockClient(c.id)}
                        data-testid={`unblock-client-${c.id}`}
                      >
                        Débloquer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ---------- Parametres ---------- */}
        <TabsContent value="parametres" className="mt-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Card className="border-border bg-card">
              <CardContent className="p-5 sm:p-6">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                  <Settings2 className="h-4 w-4 text-primary" />
                  Règles de réservation
                </h3>
                <div className="mt-5 space-y-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm">Validation manuelle des réservations sur place</Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Si activé, vous devez valider chaque réservation « sur place » avant l'envoi de
                        l'email de confirmation au client.
                      </p>
                    </div>
                    <Switch
                      checked={settingsEdits.manual_confirmation ?? settings?.manual_confirmation ?? true}
                      onCheckedChange={(v) => setSettingsEdits({ ...settingsEdits, manual_confirmation: v })}
                      data-testid="settings-manual-confirmation"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Remise en ligne (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="90"
                        placeholder={String(settings?.online_discount_percent ?? 0)}
                        value={settingsEdits.online_discount_percent ?? ""}
                        onChange={(e) => setSettingsEdits({ ...settingsEdits, online_discount_percent: e.target.value })}
                        data-testid="settings-discount"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Email société — {SITE.fullName}</Label>
                    <Input
                      type="email"
                      placeholder={settings?.[`company_email_${SITE_STUDIO_TYPE}`] || ""}
                      value={settingsEdits[`company_email_${SITE_STUDIO_TYPE}`] ?? ""}
                      onChange={(e) =>
                        setSettingsEdits({ ...settingsEdits, [`company_email_${SITE_STUDIO_TYPE}`]: e.target.value })
                      }
                      data-testid="settings-email-site"
                    />
                  </div>
                  <div className="space-y-3 border-t border-border pt-4">
                    <Label className="text-sm">Contact & réseaux sociaux</Label>
                    <p className="text-xs text-muted-foreground">
                      Affichés dans l'en-tête, le pied de page et la page Contact. Collez l'URL
                      complète du profil (https://...).
                    </p>
                    {[
                      { key: "contact_phone", label: "Téléphone affiché sur le site", type: "tel" },
                      { key: "social_instagram", label: "Instagram", type: "url" },
                      { key: "social_tiktok", label: "TikTok", type: "url" },
                      { key: "social_snapchat", label: "Snapchat", type: "url" },
                    ].map(({ key, label, type }) => (
                      <div key={key} className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <Input
                          type={type}
                          placeholder={settings?.[key] || (type === "url" ? "https://..." : "07 ...")}
                          value={settingsEdits[key] ?? ""}
                          onChange={(e) => setSettingsEdits({ ...settingsEdits, [key]: e.target.value })}
                          data-testid={`settings-${key.replace("_", "-")}`}
                        />
                      </div>
                    ))}
                  </div>
                  <Button onClick={saveSettings} data-testid="settings-save">
                    Enregistrer les paramètres
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <Card className={settings?.smtp_configured ? "border-green-600/40 bg-card" : "border-orange-500/40 bg-card"}>
                <CardContent className="p-5 sm:p-6">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                    {settings?.smtp_configured ? (
                      <ShieldCheck className="h-4 w-4 text-green-400" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-400" />
                    )}
                    Envoi d'emails
                  </h3>
                  {settings?.smtp_configured ? (
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="smtp-status">
                      Configuré — les emails sont envoyés depuis{" "}
                      <span className="font-medium text-foreground">{settings?.sender_email}</span>.
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="smtp-status">
                      <span className="font-medium text-orange-400">Non configuré.</span> Les emails sont
                      enregistrés mais pas envoyés. Fournissez le mot de passe d'application Gmail de{" "}
                      <span className="font-medium text-foreground">{settings?.sender_email}</span> à votre
                      développeur pour activer l'envoi réel.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border bg-card">
                <CardContent className="p-5 sm:p-6">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                    <Mail className="h-4 w-4 text-primary" />
                    Derniers emails
                  </h3>
                  <div className="mt-4 max-h-[320px] space-y-2 overflow-y-auto" data-testid="email-logs-list">
                    {emailLogs.length === 0 && (
                      <p className="text-sm text-muted-foreground">Aucun email pour le moment</p>
                    )}
                    {emailLogs.slice(0, 20).map((log) => (
                      <div key={log.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium">{log.subject}</p>
                          <p className="truncate text-[11px] text-muted-foreground">→ {log.to}</p>
                        </div>
                        <Badge variant="outline" className={`flex-shrink-0 text-[10px] ${EMAIL_STATUS_BADGES[log.status] || ""}`}>
                          {EMAIL_STATUS_LABELS[log.status] || log.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
